/**
 * A Diagram card's Mermaid source, drawn as design v2 asks (README, Flashcard): nodes as paper
 * boxes — 1px `--rule-strong`, radius 2px, `--paper` fill, Plex Mono 13px — with `--ink-muted`
 * arrows. Mermaid (~1 MB) loads only when a diagram card is shown, so a student who never
 * turns one never downloads it. Its output is SVG from `securityLevel: "strict"`, which
 * sanitises labels; if it cannot draw the source, the source is shown as before.
 */

import { useEffect, useId, useState } from "react";

const token = (name: string, fallback: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

export function Diagram({ source }: { source: string }) {
  const id = `diagram-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setSvg(null);
    setFailed(false);
    void (async () => {
      try {
        const { default: mermaid } = await import("mermaid");
        // Labels are measured as they are drawn; measured in a fallback font they clip.
        // A face is fetched only once something asks for it, so ask before measuring.
        await document.fonts?.load(`13px ${token("--font-mono", "monospace")}`).catch(() => []);
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
          fontFamily: token("--font-mono", "monospace"),
          // Natural size, not 100%: the card scrolls a wide diagram rather than shrinking it.
          flowchart: { useMaxWidth: false },
          sequence: { useMaxWidth: false },
          class: { useMaxWidth: false },
          state: { useMaxWidth: false },
          er: { useMaxWidth: false },
          themeVariables: {
            fontSize: "13px",
            primaryColor: token("--paper", "#faf8f3"),
            primaryTextColor: token("--ink", "#1a2422"),
            primaryBorderColor: token("--rule-strong", "#b8b0a0"),
            lineColor: token("--ink-muted", "#5a6360"),
            clusterBkg: "transparent",
            clusterBorder: token("--rule-strong", "#b8b0a0"),
          },
        });
        const out = await mermaid.render(id, source);
        if (live) setSvg(out.svg);
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [id, source]);

  if (failed) return <span className="card__mermaid">{source}</span>;
  if (svg === null) return <span className="card__diagram card__diagram--loading" aria-hidden />;
  return (
    <span
      className="card__diagram"
      role="img"
      aria-label="Diagram"
      // Mermaid's own SVG, rendered with securityLevel "strict" (labels sanitised).
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
